import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Page from '../../components/Page'
import {
  Building2,
  TrendingUp,
  Package,
  MapPin,
  Brain,
  Activity,
  AlertTriangle,
  CheckCircle,
  Info,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Map,
  DollarSign,
  RefreshCw,
  Loader2
} from 'lucide-react'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useOwner } from '../../contexts/OwnerContext'
import { AIAssistantButton } from '../../components/AIAssistant'

// Types
interface KPIs {
  total_cds: number
  active_cds: number
  inactive_cds: number
  activity_rate: number
  states_coverage: number
  avg_store_cost: number
  units_with_ia: number
  ia_adoption_rate: number
}

interface ChartData {
  name: string
  value: number
  color?: string
  fullName?: string
}

interface Insight {
  type: 'positive' | 'attention' | 'opportunity' | 'info'
  icon: string
  title: string
  description: string
  metric: string
}

interface Alert {
  type: 'success' | 'warning' | 'info' | 'error'
  title: string
  message: string
}

interface CDDetail {
  oid: number
  name: string
  tradeName: string
  code: string
  documentNumber: string
  companyName: string
  city: string
  state: string
  fullAddress: string
  hasGeolocation: boolean
  latitude?: string | number
  longitude?: string | number
  storeCost: number
  useIA: boolean
  statesServed: string[]
}

interface AnalysisData {
  kpis: KPIs
  charts: {
    state_distribution: ChartData[]
    company_distribution: ChartData[]
    ia_adoption: ChartData[]
    cost_distribution: ChartData[]
  }
  insights: Insight[]
  alerts: Alert[]
  cd_details: CDDetail[]
  geographic_coverage: {
    states_covered: string[]
    states_count: number
    cities_count: number
    coverage_percentage: number
  }
  summary: {
    total_units: number
    active_units: number
    inactive_units: number
    states_covered: number
    companies_count: number
  }
}

interface SelectedLocation {
  cd: CDDetail
  coords: {
    lat: number
    lng: number
  }
}

interface MultiStateUnit {
  oid: number
  name: string
  city: string
  state: string
  companyName: string
  statesServed: string[]
}

const DistributionCentersAnalysisPage: React.FC = () => {
  const { headers, isOwnerValid } = useOwner()
  const [data, setData] = useState<AnalysisData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAllCDs, setShowAllCDs] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(null)
  const [multiStateModalOpen, setMultiStateModalOpen] = useState(false)
  const [multiStateUnits, setMultiStateUnits] = useState<MultiStateUnit[]>([])
  const cdListRef = useRef<HTMLDivElement | null>(null)

  // Fetch analysis data
  const fetchAnalysis = async () => {
    if (!isOwnerValid || !headers?.userToken) {
      setError('Autenticação necessária')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const authToken = headers.userToken.startsWith('Bearer ')
        ? headers.userToken
        : `Bearer ${headers.userToken}`

      const response = await fetch('/api/v1/business-units/analysis', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken,
          'company': headers.company,
          'businessUnit': headers.businessUnit || '-1',
          'user': headers.user || ''
        }
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          setData(result.data)
        } else {
          setError(result.message || 'Erro ao carregar análise')
        }
      } else {
        setError(`Erro ao carregar dados (${response.status})`)
      }
    } catch (err) {
      setError('Erro de conexão com o servidor')
      console.error('Erro ao buscar análise:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAnalysis()
  }, [isOwnerValid, headers?.company])

  // Get icon for insight type
  const getInsightIcon = (iconName: string) => {
    switch (iconName) {
      case 'map': return MapPin
      case 'brain': return Brain
      case 'activity': return Activity
      case 'building': return Building2
      case 'dollar': return DollarSign
      default: return Lightbulb
    }
  }

  // Get alert icon and colors
  const getAlertStyle = (type: string) => {
    switch (type) {
      case 'success':
        return { icon: CheckCircle, bg: 'rgba(16, 185, 129, 0.1)', border: '#10b981', text: '#10b981' }
      case 'warning':
        return { icon: AlertTriangle, bg: 'rgba(245, 158, 11, 0.1)', border: '#f59e0b', text: '#f59e0b' }
      case 'error':
        return { icon: AlertTriangle, bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444', text: '#ef4444' }
      default:
        return { icon: Info, bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6', text: '#3b82f6' }
    }
  }

  // Get insight style
  const getInsightStyle = (type: string) => {
    switch (type) {
      case 'positive':
        return { bg: 'rgba(16, 185, 129, 0.1)', border: '#10b981', accent: '#10b981' }
      case 'attention':
        return { bg: 'rgba(245, 158, 11, 0.1)', border: '#f59e0b', accent: '#f59e0b' }
      case 'opportunity':
        return { bg: 'rgba(139, 92, 246, 0.1)', border: '#8b5cf6', accent: '#8b5cf6' }
      default:
        return { bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6', accent: '#3b82f6' }
    }
  }

  if (loading) {
    return (
      <Page title="🏢 Análise de Centros de Distribuição" subtitle="Carregando análise...">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <Loader2 className="h-12 w-12 animate-spin" style={{ color: 'var(--primary)' }} />
          <p style={{ color: 'var(--text-secondary)' }}>Analisando centros de distribuição...</p>
        </div>
      </Page>
    )
  }

  if (error) {
    return (
      <Page title="🏢 Análise de Centros de Distribuição" subtitle="Erro ao carregar">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <AlertTriangle className="h-12 w-12" style={{ color: '#ef4444' }} />
          <p style={{ color: 'var(--text-primary)' }}>{error}</p>
          <button
            onClick={fetchAnalysis}
            className="px-4 py-2 rounded-lg flex items-center gap-2"
            style={{ backgroundColor: 'var(--primary)', color: 'white' }}
          >
            <RefreshCw className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      </Page>
    )
  }

  if (!data) return null

  const displayedCDs = showAllCDs ? data.cd_details : data.cd_details.slice(0, 6)
  const formatCurrency = (value: number) => {
    if (!value || value <= 0) return 'Sem custo'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }
  const cdCardAccents = ['#06b6d4', '#8b5cf6', '#f97316', '#10b981', '#3b82f6', '#eab308']
  const getCDAccent = (index: number) => cdCardAccents[index % cdCardAccents.length]
  const parseCoordinate = (value?: string | number) => {
    if (value === null || value === undefined || value === '') return null
    if (typeof value === 'number') return value
    const normalized = value.replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : null
  }
  const normalizeCoordinates = (cd: CDDetail) => {
    const lat = parseCoordinate(cd.latitude)
    const lng = parseCoordinate(cd.longitude)
    if (lat === null || lng === null) return null

    const inBrazil = (testLat: number, testLng: number) =>
      testLat >= -35 && testLat <= 6 && testLng >= -75 && testLng <= -30

    if (inBrazil(lat, lng)) return { lat, lng }
    if (inBrazil(lng, lat)) return { lat: lng, lng: lat }

    if (lat < 0 && lng > 0 && inBrazil(lat, -lng)) {
      return { lat, lng: -lng }
    }

    if (lat > 0 && lng < 0 && inBrazil(-lat, lng)) {
      return { lat: -lat, lng }
    }

    if (lat > 0 && lng > 0 && inBrazil(-lat, -lng)) {
      return { lat: -lat, lng: -lng }
    }

    return null
  }

  const coveragePoints = data.cd_details
    .map((cd) => {
      const coords = normalizeCoordinates(cd)
      return coords ? { ...coords, name: cd.name } : null
    })
    .filter((item): item is { lat: number; lng: number; name: string } => Boolean(item))


  return (
    <Page
      title="🏢 Análise de Centros de Distribuição"
      subtitle={`${data.kpis.total_cds} centros analisados em ${data.kpis.states_coverage} estados`}
      onRefresh={fetchAnalysis}
    >
      <div className="space-y-8">
        {/* AI Assistant Button */}
        <AIAssistantButton screenContext="DistributionCenters" />

        {/* KPIs Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total CDs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 rounded-xl border-2"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-primary)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total de CDs</p>
                <p className="text-3xl font-bold mt-1" style={{ color: 'var(--text-primary)' }}>
                  {data.kpis.total_cds}
                </p>
                <p className="text-sm mt-2" style={{ color: '#10b981' }}>
                  {data.kpis.active_cds} ativos
                </p>
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                <Building2 className="h-8 w-8" style={{ color: '#3b82f6' }} />
              </div>
            </div>
          </motion.div>

          {/* Coverage */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`p-6 rounded-xl border-2 transition-all ${coveragePoints.length ? 'cursor-pointer hover:shadow-lg' : ''}`}
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-primary)' }}
            onClick={() => {
              if (coveragePoints.length > 0) {
                cdListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Cobertura</p>
                <p className="text-3xl font-bold mt-1" style={{ color: 'var(--text-primary)' }}>
                  {data.geographic_coverage.coverage_percentage}%
                </p>
                <p className="text-sm mt-2" style={{ color: '#10b981' }}>
                  {data.kpis.states_coverage} estados
                </p>
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                <MapPin className="h-8 w-8" style={{ color: '#10b981' }} />
              </div>
            </div>
          </motion.div>

          {/* Activity Rate */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-6 rounded-xl border-2"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-primary)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Taxa de Atividade</p>
                <p className="text-3xl font-bold mt-1" style={{ color: 'var(--text-primary)' }}>
                  {data.kpis.activity_rate}%
                </p>
                <p className="text-sm mt-2" style={{ color: data.kpis.inactive_cds > 0 ? '#f59e0b' : '#10b981' }}>
                  {data.kpis.inactive_cds} inativos
                </p>
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                <Activity className="h-8 w-8" style={{ color: '#8b5cf6' }} />
              </div>
            </div>
          </motion.div>

          {/* IA Adoption */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 rounded-xl border-2"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-primary)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Adoção de IA</p>
                <p className="text-3xl font-bold mt-1" style={{ color: 'var(--text-primary)' }}>
                  {data.kpis.units_with_ia}
                </p>
                <p className="text-sm mt-2" style={{ color: '#f59e0b' }}>
                  {data.kpis.ia_adoption_rate}% dos CDs
                </p>
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                <Brain className="h-8 w-8" style={{ color: '#f59e0b' }} />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Distribution by State */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="p-6 rounded-xl border-2"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-primary)' }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Distribuição por Estado
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.charts.state_distribution.slice(0, 10)} layout="vertical">
                  <XAxis type="number" stroke="var(--text-secondary)" />
                  <YAxis dataKey="name" type="category" width={40} stroke="var(--text-secondary)" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="value" name="CDs" radius={[0, 4, 4, 0]}>
                    {data.charts.state_distribution.slice(0, 10).map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {(() => {
              const byState = (data.cd_details || []).reduce<Record<string, string[]>>((acc, cd) => {
                const stateKey = cd.state && cd.state !== 'N/D' ? cd.state : 'N/D'
                if (!acc[stateKey]) acc[stateKey] = []
                acc[stateKey].push(cd.name)
                return acc
              }, {})

              const orderedStates = Object.entries(byState)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([state, cds]) => ({ state, cds }))

              return (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                    CDs por estado
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {orderedStates.map(({ state, cds }) => (
                      <div
                        key={state}
                        className="p-4 rounded-xl border"
                        style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)' }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                            {state}
                          </div>
                          <span
                            className="text-xs font-semibold px-2 py-1 rounded-full"
                            style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}
                          >
                            {cds.length} CDs
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          {cds.map((name) => (
                            <div key={name} className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--text-secondary)' }} />
                              <span>{name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {orderedStates.length === 0 && (
                      <div style={{ color: 'var(--text-secondary)' }}>Nenhuma unidade disponível.</div>
                    )}
                  </div>
                </div>
              )
            })()}
          </motion.div>

          {/* IA Adoption Pie Chart */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="p-6 rounded-xl border-2"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-primary)' }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Adoção de Inteligência Artificial
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.charts.ia_adoption}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {data.charts.ia_adoption.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {(() => {
              const unitsWithIA = data.cd_details.filter((cd) => cd.useIA)
              const unitsWithoutIA = data.cd_details.filter((cd) => !cd.useIA)

              return (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    className="p-4 rounded-xl border"
                    style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)' }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)' }}>
                          <Brain className="h-4 w-4" style={{ color: '#10b981' }} />
                        </div>
                        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          Unidades com IA
                        </h4>
                      </div>
                      <span
                        className="text-xs font-semibold px-2 py-1 rounded-full"
                        style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}
                      >
                        {unitsWithIA.length}
                      </span>
                    </div>
                    <div className="max-h-44 overflow-auto text-sm space-y-1">
                      {unitsWithIA.map((cd) => (
                        <div key={cd.oid} className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#10b981' }} />
                          <span>{cd.name}</span>
                        </div>
                      ))}
                      {unitsWithIA.length === 0 && (
                        <div style={{ color: 'var(--text-secondary)' }}>Nenhuma unidade com IA.</div>
                      )}
                    </div>
                  </div>

                  <div
                    className="p-4 rounded-xl border"
                    style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)' }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(148, 163, 184, 0.2)' }}>
                          <Activity className="h-4 w-4" style={{ color: 'var(--text-secondary)' }} />
                        </div>
                        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          Unidades sem IA
                        </h4>
                      </div>
                      <span
                        className="text-xs font-semibold px-2 py-1 rounded-full"
                        style={{ backgroundColor: 'rgba(148, 163, 184, 0.2)', color: 'var(--text-secondary)' }}
                      >
                        {unitsWithoutIA.length}
                      </span>
                    </div>
                    <div className="max-h-44 overflow-auto text-sm space-y-1">
                      {unitsWithoutIA.map((cd) => (
                        <div key={cd.oid} className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--text-secondary)' }} />
                          <span>{cd.name}</span>
                        </div>
                      ))}
                      {unitsWithoutIA.length === 0 && (
                        <div style={{ color: 'var(--text-secondary)' }}>Todas as unidades usam IA.</div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })()}
          </motion.div>
        </div>

        {/* Strategic Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="space-y-4"
        >
          <h2 className="text-xl font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Lightbulb className="h-6 w-6" style={{ color: '#f59e0b' }} />
            Insights Estratégicos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {data.insights.map((insight, index) => {
              const style = getInsightStyle(insight.type)
              const IconComponent = getInsightIcon(insight.icon)
              const isMultiStateInsight = insight.title === 'Atendimento Multiestado'

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className={`p-5 rounded-xl border-2 transition-all hover:shadow-lg ${isMultiStateInsight ? 'cursor-pointer' : ''}`}
                  style={{
                    backgroundColor: style.bg,
                    borderColor: style.border
                  }}
                  onClick={() => {
                    if (!isMultiStateInsight) return
                    const units = data.cd_details
                      .filter((unit) => (unit.statesServed || []).length >= 3)
                      .map((unit) => ({
                        oid: unit.oid,
                        name: unit.name,
                        city: unit.city,
                        state: unit.state,
                        companyName: unit.companyName,
                        statesServed: unit.statesServed || []
                      }))
                    setMultiStateUnits(units)
                    setMultiStateModalOpen(true)
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: style.accent + '20' }}>
                      <IconComponent className="h-5 w-5" style={{ color: style.accent }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {insight.title}
                        </h4>
                        <span className="text-sm font-bold px-3 py-1 rounded-full" style={{ backgroundColor: style.accent + '30', color: style.accent }}>
                          {insight.metric}
                        </span>
                      </div>
                      <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                        {insight.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Alerts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="space-y-4"
        >
          <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            Alertas e Notificações
          </h2>
          <div className="space-y-3">
            {data.alerts.map((alert, index) => {
              const style = getAlertStyle(alert.type)
              const IconComponent = style.icon

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 + index * 0.1 }}
                  className="p-4 rounded-xl border-l-4 flex items-start gap-4"
                  style={{
                    backgroundColor: style.bg,
                    borderLeftColor: style.border
                  }}
                >
                  <IconComponent className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: style.text }} />
                  <div>
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {alert.title}
                    </h4>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                      {alert.message}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* CD Details List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="space-y-4"
          ref={cdListRef}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
              Lista de Centros de Distribuição
            </h2>
            <span className="text-sm px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
              {data.cd_details.length} unidades
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayedCDs.map((cd, index) => {
              const accent = getCDAccent(index)
              return (
                <motion.div
                  key={cd.oid}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2 + index * 0.05 }}
                  className="p-5 rounded-2xl border-2 transition-all hover:shadow-2xl hover:-translate-y-0.5 overflow-hidden relative"
                  style={{
                    background: `linear-gradient(160deg, ${accent}1f 0%, var(--bg-card) 40%, var(--bg-secondary) 100%)`,
                    borderColor: `color-mix(in srgb, ${accent} 35%, var(--border-primary))`,
                    boxShadow: `0 10px 30px -20px ${accent}66`
                  }}
                >
                  <div
                    className="absolute inset-x-0 top-0 h-1.5"
                    style={{ background: `linear-gradient(90deg, ${accent}, ${accent}80)` }}
                  />
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="p-2.5 rounded-xl border"
                      style={{
                        background: `linear-gradient(135deg, ${accent}2a, ${accent}0f)`,
                        borderColor: `color-mix(in srgb, ${accent} 40%, var(--border-primary))`
                      }}
                    >
                      <Building2 className="h-5 w-5" style={{ color: accent }} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                        {cd.name}
                      </h4>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {cd.code || 'Sem código'}
                      </p>
                    </div>
                  </div>
                  {cd.useIA && (
                    <div
                      className="p-1.5 rounded-lg border"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.16)',
                        borderColor: 'rgba(16, 185, 129, 0.35)'
                      }}
                      title="IA ativa"
                    >
                      <Brain className="h-4 w-4" style={{ color: '#10b981' }} />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {cd.city || 'N/D'}{cd.state && cd.state !== 'N/D' ? ` - ${cd.state}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {cd.companyName}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div
                    className="p-2 rounded-lg border"
                    style={{
                      background: `linear-gradient(180deg, ${accent}10, var(--bg-card))`,
                      borderColor: `color-mix(in srgb, ${accent} 18%, var(--border-primary))`
                    }}
                  >
                    <div className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                      <Map className="h-3 w-3" />
                      Abrangência
                    </div>
                    <div className="text-xs font-semibold mt-1" style={{ color: 'var(--text-primary)' }}>
                      {cd.statesServed?.length || 0} estados
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!cd.hasGeolocation) return
                      const coords = normalizeCoordinates(cd)
                      if (coords) {
                        setSelectedLocation({ cd, coords })
                      }
                    }}
                    className={`p-2 rounded-lg border text-left transition-all ${cd.hasGeolocation ? 'cursor-pointer hover:-translate-y-0.5' : 'cursor-not-allowed opacity-60'}`}
                    style={{
                      background: `linear-gradient(180deg, ${accent}10, var(--bg-card))`,
                      borderColor: `color-mix(in srgb, ${accent} 18%, var(--border-primary))`
                    }}
                    title={cd.hasGeolocation ? 'Ver no mapa' : 'Sem geolocalização'}
                  >
                    <div className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                      <MapPin className="h-3 w-3" />
                      Geolocalização
                    </div>
                    <div className="text-xs font-semibold mt-1" style={{ color: cd.hasGeolocation ? '#10b981' : 'var(--text-secondary)' }}>
                      {cd.hasGeolocation ? 'Ativa' : 'Indisponível'}
                    </div>
                  </button>
                  <div
                    className="p-2 rounded-lg border"
                    style={{
                      background: `linear-gradient(180deg, ${accent}10, var(--bg-card))`,
                      borderColor: `color-mix(in srgb, ${accent} 18%, var(--border-primary))`
                    }}
                  >
                    <div className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                      <DollarSign className="h-3 w-3" />
                      Custo
                    </div>
                    <div className="text-xs font-semibold mt-1" style={{ color: 'var(--text-primary)' }}>
                      {formatCurrency(cd.storeCost)}
                    </div>
                  </div>
                </div>

                {cd.statesServed && cd.statesServed.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-4">
                    {cd.statesServed.map((state, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-0.5 rounded-full border"
                        style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
                      >
                        {state}
                      </span>
                    ))}
                  </div>
                )}
                </motion.div>
              )
            })}
          </div>

          {data.cd_details.length > 6 && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={() => setShowAllCDs(!showAllCDs)}
              className="w-full py-3 rounded-xl border-2 flex items-center justify-center gap-2 transition-all hover:shadow-md"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border-primary)',
                color: 'var(--text-primary)'
              }}
            >
              {showAllCDs ? (
                <>
                  <ChevronUp className="h-5 w-5" />
                  Mostrar menos
                </>
              ) : (
                <>
                  <ChevronDown className="h-5 w-5" />
                  Mostrar todos ({data.cd_details.length - 6} restantes)
                </>
              )}
            </motion.button>
          )}
        </motion.div>

        {/* Geolocation Modal */}
        <AnimatePresence>
          {selectedLocation && (() => {
            const { cd, coords } = selectedLocation
            const { lat, lng } = coords
            const delta = 0.03
            const bbox = `${lng - delta},${lat - delta},${lng + delta},${lat + delta}`
            const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat},${lng}`

            return (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                onClick={() => setSelectedLocation(null)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="w-full max-w-4xl rounded-3xl overflow-hidden"
                  style={{ backgroundColor: 'var(--bg-secondary)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div
                    className="px-8 py-6 flex items-center justify-between relative overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, #1E40AF 0%, #1E3A8A 50%, #1E40AF 100%)',
                      boxShadow: '0 10px 30px rgba(30, 64, 175, 0.3)'
                    }}
                  >
                    <div
                      className="absolute inset-0 opacity-10"
                      style={{
                        backgroundImage: 'radial-gradient(circle at 20% 50%, white 0%, transparent 50%)',
                        backgroundSize: '200% 200%'
                      }}
                    />
                    <div className="relative z-10">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-1 h-8 rounded-full" style={{ backgroundColor: '#FBBF24' }} />
                        <h3 className="text-2xl font-bold text-white">
                          Geolocalização da Unidade
                        </h3>
                      </div>
                      <p className="text-white text-opacity-90 text-sm font-medium">
                        {cd.name}
                      </p>
                      <p className="text-white text-opacity-70 text-xs">
                        {cd.city}{cd.state && cd.state !== 'N/D' ? ` - ${cd.state}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedLocation(null)}
                      className="px-4 py-2 rounded-xl transition-all duration-200 relative z-10 hover:bg-white hover:bg-opacity-20 text-white"
                    >
                      Fechar
                    </button>
                  </div>
                  <div className="h-[440px] w-full">
                    <iframe
                      title={`Mapa ${cd.name}`}
                      src={mapSrc}
                      className="w-full h-full"
                      style={{ border: 0 }}
                      loading="lazy"
                    />
                  </div>
                  <div className="px-6 py-3 border-t text-sm" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}>
                    Lat: {lat.toFixed(6)} | Long: {lng.toFixed(6)}
                  </div>
                </motion.div>
              </motion.div>
            )
          })()}
        </AnimatePresence>


        {/* Multi-state Units Modal */}
        <AnimatePresence>
          {multiStateModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
              onClick={() => setMultiStateModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="w-full max-w-5xl rounded-3xl overflow-hidden"
                style={{ backgroundColor: 'var(--bg-secondary)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                onClick={(event) => event.stopPropagation()}
              >
                <div
                  className="px-8 py-6 flex items-center justify-between relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #1E40AF 0%, #1E3A8A 50%, #1E40AF 100%)',
                    boxShadow: '0 10px 30px rgba(30, 64, 175, 0.3)'
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-10"
                    style={{
                      backgroundImage: 'radial-gradient(circle at 20% 50%, white 0%, transparent 50%)',
                      backgroundSize: '200% 200%'
                    }}
                  />
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-1 h-8 rounded-full" style={{ backgroundColor: '#FBBF24' }} />
                      <h3 className="text-2xl font-bold text-white">
                        Unidades com Atendimento Multiestado
                      </h3>
                    </div>
                    <p className="text-white text-opacity-90 text-sm font-medium">
                      {multiStateUnits.length} unidades atendem 3 ou mais estados.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMultiStateModalOpen(false)}
                    className="px-4 py-2 rounded-xl transition-all duration-200 relative z-10 hover:bg-white hover:bg-opacity-20 text-white"
                  >
                    Fechar
                  </button>
                </div>

                <div className="flex-1 overflow-auto px-8 py-6">
                  {multiStateUnits.length > 0 ? (
                    <div className="overflow-hidden rounded-2xl border" style={{ borderColor: 'var(--border-primary)' }}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr
                            style={{
                              background: 'linear-gradient(90deg, #1E40AF 0%, #1E3A8A 100%)',
                              color: 'white',
                              borderBottom: '2px solid #1E40AF'
                            }}
                          >
                            <th className="text-left py-4 px-6 font-bold text-xs uppercase tracking-wider text-white">
                              Unidade
                            </th>
                            <th className="text-left py-4 px-6 font-bold text-xs uppercase tracking-wider text-white">
                              Empresa
                            </th>
                            <th className="text-left py-4 px-6 font-bold text-xs uppercase tracking-wider text-white">
                              Cidade
                            </th>
                            <th className="text-left py-4 px-6 font-bold text-xs uppercase tracking-wider text-white">
                              Estados atendidos
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {multiStateUnits.map((unit, idx) => (
                            <tr
                              key={unit.oid}
                              style={{
                                borderBottom: '1px solid var(--border-primary)',
                                backgroundColor: idx % 2 === 0 ? 'var(--bg-secondary)' : 'rgba(30, 64, 175, 0.02)'
                              }}
                            >
                              <td className="py-4 px-6 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {unit.name}
                              </td>
                              <td className="py-4 px-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {unit.companyName}
                              </td>
                              <td className="py-4 px-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {unit.city}{unit.state && unit.state !== 'N/D' ? ` - ${unit.state}` : ''}
                              </td>
                              <td className="py-4 px-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {unit.statesServed.join(', ')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-20">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                        style={{ backgroundColor: 'rgba(30, 64, 175, 0.1)' }}
                      >
                        <Map className="w-8 h-8" style={{ color: 'var(--text-secondary)' }} />
                      </div>
                      <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                        Nenhuma unidade encontrada
                      </p>
                      <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                        Não há CDs com atendimento multiestado no momento.
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </Page>
  )
}

export default DistributionCentersAnalysisPage
